import Image from 'next/image';
import Link from 'next/link';
import { pickImageUrl, type DerivedImageUrls } from '@/lib/mediaImages';
import { focalToObjectPosition, type ImageFocal } from '@/lib/imageFocal';

export default function MemberCard({
  name,
  membershipNumber,
  title,
  photoUrl,
  photoUrls,
  photoFocal,
  profile,
  shortIntroduction,
  id,
}: {
  name: string;
  membershipNumber?: string | null;
  title?: string | null;
  photoUrl?: string | null;
  photoUrls?: DerivedImageUrls | null;
  photoFocal?: ImageFocal | null;
  profile?: string;
  shortIntroduction?: string | null;
  id: string;
}) {
  // メンバー一覧用紹介文が設定されている場合はそれを使用、なければprofileから30文字を切り取り
  // 改行を保持するため、trim()は先頭と末尾のみ削除（内部的には改行が残る）
  // \n が文字列リテラルとして保存されている場合は改行文字に変換
  const snippet = shortIntroduction
    ? shortIntroduction.trim().replace(/\\n/g, '\n').replace(/\\r/g, '')
    : profile
    ? profile.replace(/\s+/g, ' ').trim().slice(0, 30)
    : undefined;
  // 中央クロップ済みの派生画像ではなく元画像を使う（顔の欠けを二重に起こさないため）
  const photoSrc = pickImageUrl(photoUrls, photoUrl ?? null, 'original');
  // 会員が設定した表示位置。未設定なら従来どおり上寄せ（顔は写真上部にあることが多い）
  const objectPosition = focalToObjectPosition(photoFocal, 'center top');

  return (
    <article
      data-animate='fade-up'
      className='flex h-full flex-col rounded-none border border-line bg-[#fdfbf7] shadow-[0_6px_18px_rgba(0,0,0,0.08)] yui-hover-card'
    >
      <div className='flex flex-1 flex-col items-center pb-6 text-center'>
        <div className='mb-5 w-full rounded border border-line shadow-inner'>
          {/*
            目的: 円形表示だった頃と同等に顔が見えるようにする
            方針:
            - 枠は 3:2。縦長写真は縦方向にはみ出すため、会員が設定した焦点位置で見せたい点を残す
            - 未設定なら従来どおり上寄せ（center top）にフォールバックする
            - card16x9 派生はサーバ側で「中央クロップ」されており顔が欠けるので使わない。original を使う
            - original は未リサイズのことがあるため next/image の最適化に載せる（unoptimized を付けない）
          */}
          <div className='relative w-full overflow-hidden bg-gray-100 aspect-[3/2]'>
            {photoSrc ? (
              <Image
                src={photoSrc}
                alt={name}
                fill
                sizes='(min-width: 1024px) 300px, (min-width: 768px) 45vw, 100vw'
                className='object-cover'
                style={{ objectPosition }}
                quality={75}
              />
            ) : (
              <div className='relative flex h-full w-full items-center justify-center bg-white'>
                <Image
                  src='/assets/img/logo-yuikai.png'
                  alt='Yuikai'
                  width={120}
                  height={120}
                  className='h-20 w-20 object-contain opacity-80'
                  quality={75}
                />
              </div>
            )}
          </div>
        </div>
        {membershipNumber ? (
          <p className='mb-1 text-[11px] font-semibold tracking-wider text-gray-400'>
            No. {membershipNumber}
          </p>
        ) : null}
        <h3 className='font-serif text-[20px] text-gray-800'>{name}</h3>
        {title ? (
          <p className='mt-1 text-[14px] font-semibold text-primary px-2'>
            {title}
          </p>
        ) : null}
        {snippet ? (
          <p className='mt-4 px-4 text-[13px] leading-6 text-gray-600 whitespace-pre-line'>
            {snippet}
            {!shortIntroduction && profile && profile.length > 30 ? '…' : ''}
          </p>
        ) : null}
        <div className='mt-6 w-full max-w-[220px]'>
          <Link
            href={`/members/${id}`}
            className='inline-flex items-center justify-center rounded-full border-2 border-primary px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-white'
          >
            プロフィールを見る
          </Link>
        </div>
      </div>
    </article>
  );
}
